import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as productService from '../lib/services/productService';
import { useCart } from '../context/CartContext';
import { useCustomerType } from '../context/CustomerTypeContext';
import { hasHtmlTags, sanitizeRichText } from '../lib/richText';
import './ProductDetail.css';


const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartItems } = useCart();
  const { isPrivate, isCorporate } = useCustomerType();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);


  const fetchProduct = useCallback(async () => {
    try {
      const product = await productService.getProductById(id);
      setProduct(product);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  }, [id]);


  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIndex(0);
  }, [id]);


  const getProductQuantityInCart = (productId) => {
    const cartItem = cartItems.find((item) => item.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  const formatIngredientsHtml = (ingredients) => {
    const sanitized = sanitizeRichText(ingredients || '');
    if (!sanitized) {
      return '';
    }

    if (hasHtmlTags(sanitized)) {
      return sanitized;
    }

    return sanitized.replace(/\n/g, '<br />');
  };


  const isProductNew = (createdAt) => {
    if (!createdAt) return false;
    const productDate = new Date(createdAt);
    const currentDate = new Date();
    const twoMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 1));
    return productDate > twoMonthsAgo;
  };


  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
      setQuantity(1);
    }
  };

  const getProductImages = (productData) => {
    if (Array.isArray(productData?.images) && productData.images.length > 0) {
      return productData.images.filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.trim());
    }

    if (productData?.image_url) {
      return [productData.image_url];
    }

    return [];
  };

  // compute availability text
  const availableStock = product ? product.stock_quantity - (product.defective_count || 0) : 0;
  const availabilityMessage = availableStock <= 0
    ? 'Out of stock'
    : availableStock < 20
      ? 'Very few left'
      : '';
  const productImages = getProductImages(product);
  const currentImage = productImages.length > 0
    ? productImages[selectedImageIndex]
    : null;

  const prevImage = () => setSelectedImageIndex((i) => (i - 1 + productImages.length) % productImages.length);
  const nextImage = () => setSelectedImageIndex((i) => (i + 1) % productImages.length);


  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }


  if (!product) {
    return (
      <div className="container">
        <div className="no-product">
          <h2>Product not found</h2>
          <button onClick={() => navigate('/products')} className="btn btn-primary">
            Back to Products
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="product-detail-page">
      <section className="section">
        <div className="container">
          <button className="btn btn-outline" style={{ marginBottom: '20px' }} onClick={() => navigate('/products')}>
            ← Back to Products
          </button>
          <div className="product-detail-content">
            <div className="product-detail-image-section">
              <div className="product-detail-image">
              {currentImage ? (
                <img src={currentImage} alt={product.name} />
              ) : (
                <div className="image-placeholder" aria-label="No image available">No Image Available</div>
              )}
              {productImages.length > 1 && (
                <>
                  <button className="detail-nav-btn detail-nav-prev" onClick={prevImage} aria-label="Previous image">&#8249;</button>
                  <button className="detail-nav-btn detail-nav-next" onClick={nextImage} aria-label="Next image">&#8250;</button>
                </>
              )}
              {isProductNew(product.created_at) && (
                <span className="new-badge">⭐ New</span>
              )}
              {product.gluten_free && (
                <span className="gluten-free-tag">Gluten Free</span>
              )}
              {(() => {
                const available = product.stock_quantity - (product.defective_count || 0);
                if (available <= 0) {
                  return <span className="low-stock-ribbon">Out of stock</span>;
                }
                if (available > 0 && available < 20) {
                  return <span className="low-stock-ribbon">Very few left</span>;
                }
                return null;
              })()}
              </div>
              {productImages.length > 1 && (
                <div className="product-thumbnail-strip">
                  {productImages.map((imgUrl, idx) => (
                    <button
                      key={idx}
                      className={`product-thumbnail-item ${idx === selectedImageIndex ? 'active' : ''}`}
                      onClick={() => setSelectedImageIndex(idx)}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <img src={imgUrl} alt={`${product.name} ${idx + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-detail-info">
              <h1>{product.name}</h1>
              <p className="product-category">{product.category}</p>
              {product.subcategory && (
                <p className="product-subcategory">{product.subcategory}</p>
              )}
              {availabilityMessage && (
                <p className="availability-message">{availabilityMessage}</p>
              )}
              {product.weight && product.weight > 0 && (
                <p className="product-weight" style={{ fontSize: '1em', color: '#666', margin: '10px 0', fontWeight: '500' }}>
                  📦 Weight: {product.weight}g
                </p>
              )}
              {isPrivate && (
                <>
                  {product.discounted_price && product.discounted_price < product.price ? (
                    <div className="price-with-discount">
                      <p className="product-price-large">
                        <span className="old-price">kr {Number(product.price).toFixed(2)} incl. VAT</span>
                        <span className="new-price large">kr {Number(product.discounted_price).toFixed(2)} incl. VAT</span>
                      </p>
                    </div>
                  ) : (
                    <p className="product-price-large">kr {Number(product.price).toFixed(2)} incl. VAT</p>
                  )}
                </>
              )}


              {product.description && (
                <div className="product-description-full">
                  <h3>Description</h3>
                  <p>{product.description}</p>
                </div>
              )}


              {product.ingredients && (
                <div className="product-ingredients">
                  <h3>Ingredients</h3>
                  <div
                    className="product-ingredients-content"
                    dangerouslySetInnerHTML={{ __html: formatIngredientsHtml(product.ingredients) }}
                  />
                </div>
              )}


              <div className="product-actions">
                {isPrivate && (
                  <>
                    <div className="quantity-selector">
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="quantity-btn"
                        disabled={(product.stock_quantity - (product.defective_count || 0)) <= 0}
                      >
                        −
                      </button>
                      <span className="quantity-display">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity(quantity + 1)}
                        className="quantity-btn"
                        disabled={(product.stock_quantity - (product.defective_count || 0)) <= 0}
                      >
                        +
                      </button>
                    </div>
                    <button 
                      onClick={handleAddToCart} 
                      className="btn btn-primary add-to-cart-btn"
                      disabled={(product.stock_quantity - (product.defective_count || 0)) <= 0}
                    >
                      🛒 {(product.stock_quantity - (product.defective_count || 0)) <= 0 ? 'Out of Stock' : 'Add to Cart'}
                      {getProductQuantityInCart(product.id) > 0 && (product.stock_quantity - (product.defective_count || 0)) > 0 && (
                        <span className="cart-quantity-badge">{getProductQuantityInCart(product.id)}</span>
                      )}
                    </button>
                  </>
                )}
                {isCorporate && (
                  <div className="corporate-contact">
                    <p style={{ fontSize: '1.1em', color: '#666', marginBottom: '15px' }}>
                      For corporate pricing and bulk orders, please contact us.
                    </p>
                    <Link to="/contact" className="btn btn-primary">
                      Contact Us
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};


export default ProductDetail;





